/**
 * TrendPanel — the A2 trend section for ONE metric (composed below the matrix like
 * DrillPanel): header (metric · site · window, close), the DQ annotation (OPEX visibly
 * reads KNOWN-WRONG), the TrendChart, and an sr-only data table that is the chart's
 * text alternative and the deterministic test surface (SVG geometry is not asserted).
 * Renders from a prebuilt TrendSeries — typed props only (CD-7), no fetch, no numbers.
 */

import React from 'react'
import { TrendingUp, X } from 'lucide-react'
import { dqBadge, EM_DASH, formatCellValue } from './cellFormat'
import TrendChart from './TrendChart'
import TrustChip from './TrustChip'
import type { TrendSeries } from './trendSeries'

interface TrendPanelProps {
  series: TrendSeries
  periodFrom: string
  periodTo: string
  onClose: () => void
}

function TrendHeader({ series, periodFrom, periodTo, onClose }: TrendPanelProps): React.JSX.Element {
  const badge = dqBadge(series.dqFlags)
  return (
    <header className="flex items-center justify-between gap-2">
      <h2 className="flex flex-wrap items-center gap-1.5 text-sm font-semibold text-ink">
        <TrendingUp size={14} className="text-mut2" />
        Trend — {series.title}
        <span className="font-normal text-mut">
          {series.site} · {periodFrom} → {periodTo}
        </span>
        {badge && <TrustChip tone={badge.tone} label={badge.label} title={series.dqFlags.join(' · ')} />}
      </h2>
      <button
        type="button"
        onClick={onClose}
        aria-label="Close trend"
        className="rounded p-1 text-mut hover:bg-panel2 hover:text-ink"
      >
        <X size={14} />
      </button>
    </header>
  )
}

/** sr-only text alternative for the SVG chart, and the tests' deterministic surface. */
function TrendDataTable({ series }: { series: TrendSeries }): React.JSX.Element {
  return (
    <table data-testid="trend-data" className="sr-only">
      <caption>{series.title} trend — Actual vs Target by period</caption>
      <thead>
        <tr>
          <th>Period</th>
          <th>Actual</th>
          <th>Target</th>
          <th>State</th>
        </tr>
      </thead>
      <tbody>
        {series.points.map((point) => (
          <tr
            key={point.period}
            data-testid={`trend-row-${point.period}`}
            data-rag={point.cell ? (point.cell.value === null ? 'none' : point.cell.rag) : 'unserved'}
            data-lock={point.cell?.lock_state ?? 'unserved'}
          >
            <td>{point.label}</td>
            <td>{point.cell ? formatCellValue(point.cell) : EM_DASH}</td>
            <td>{point.targetCell ? formatCellValue(point.targetCell) : EM_DASH}</td>
            <td>{point.cell?.lock_state ?? 'not served'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export default function TrendPanel(props: TrendPanelProps): React.JSX.Element {
  const { series } = props
  return (
    <section
      data-testid="trend-panel"
      aria-label={`Trend: ${series.title}`}
      className="cx-fade space-y-2 rounded-xl border border-line bg-panel p-4"
    >
      <TrendHeader {...props} />
      {series.servedCount === 0 ? (
        <p data-testid="trend-empty" className="px-1 py-6 text-center text-sm text-mut2">
          No {series.title} values served for {series.site} over {props.periodFrom} → {props.periodTo}.
        </p>
      ) : (
        <>
          <TrendChart series={series} />
          <TrendDataTable series={series} />
        </>
      )}
    </section>
  )
}
